import { exhaustiveGuard } from '@libs/utils';
import type { IngredientId } from '@libs/trip-planner-interfaces';

import { ItinerarySegmentType } from './itinerarySegmentType';
import type {
  ItineraryNoTransportSegment,
  ItineraryStenaUpsellNoTransportSegment,
  ItineraryUpsellNoTransportSegment,
} from './noTransport';
import type { ItinerarySimpleTransportSegment } from './typeGuards';
import type {
  ItineraryEndLocationSegment,
  ItineraryStartLocationSegment,
  LocationSegment,
} from './location';
import type { ProductCardTopBarType } from './shared';
import type { ItineraryOwnStaySegment } from './ownStay';

export interface ItineraryMiddleLocationLoaderSegment {
  segmentType: ItinerarySegmentType.MiddleLocation;
  location: LocationSegment;
  ingredientId: IngredientId;
  numberOfNights?: number;
}

interface ItineraryTransportLoaderSegmentBase {
  ingredientId: IngredientId;
  isLastSegment?: boolean;
}

export type ItineraryNoTransportLoaderSegment = ItineraryNoTransportSegment;

export type ItinerarySimpleTransportLoaderSegment =
  ItinerarySimpleTransportSegment;

export interface ItineraryFlightLoaderSegment
  extends ItineraryTransportLoaderSegmentBase {
  segmentType: ItinerarySegmentType.Flight;
  barType?: ProductCardTopBarType;
}

export interface ItineraryFerryLoaderSegment
  extends ItineraryTransportLoaderSegmentBase {
  segmentType: ItinerarySegmentType.Ferry;
  barType?: ProductCardTopBarType;
}

export interface ItineraryDateRangeLoader {
  segmentType: ItinerarySegmentType.DateRange;
}

export type ItineraryOwnStayLoaderSegment = ItineraryOwnStaySegment;

export interface ItineraryAccommodationLoaderSegment {
  segmentType: ItinerarySegmentType.Accommodation;
  barType: ProductCardTopBarType;
  ingredientId: IngredientId;
  ingredientIndex: number;
}

export interface ItineraryActivityLoaderSegment {
  segmentType: ItinerarySegmentType.Activity;
  barType: undefined;
  ingredientId: IngredientId;
  ingredientIndex: number;
}

export interface ItineraryUpsellLoaderSegment {
  segmentType: ItinerarySegmentType.Upsell;
  ingredientId: IngredientId;
}

export type ItineraryLoaderSegment =
  | ItineraryNoTransportLoaderSegment
  | ItineraryDateRangeLoader
  | ItineraryLocationLoaderSegment
  | ItinerarySimpleTransportLoaderSegment
  | ItineraryOwnStayLoaderSegment
  | ItineraryBookableProductLoaderSegment
  | ItineraryUpsellLoaderSegment
  | ItineraryStenaUpsellNoTransportSegment
  | ItineraryUpsellNoTransportSegment;

export type ItineraryLocationLoaderSegment =
  | ItineraryStartLocationSegment
  | ItineraryMiddleLocationLoaderSegment
  | ItineraryEndLocationSegment;

export type ItineraryBookableProductLoaderSegment =
  | ItineraryAccommodationLoaderSegment
  | ItineraryActivityLoaderSegment
  | ItineraryBookableTransportLoaderSegment;

export type ItineraryBookableTransportLoaderSegment =
  | ItineraryFerryLoaderSegment
  | ItineraryFlightLoaderSegment;

export type ItineraryTransportLoaderSegment =
  | ItineraryBookableTransportLoaderSegment
  | ItinerarySimpleTransportLoaderSegment;

export function isItinerarySimpleTransportLoaderSegment(
  segment: ItineraryLoaderSegment
): segment is ItinerarySimpleTransportLoaderSegment {
  switch (segment.segmentType) {
    case ItinerarySegmentType.OwnStay:
    case ItinerarySegmentType.Accommodation:
    case ItinerarySegmentType.Activity:
    case ItinerarySegmentType.StartLocation:
    case ItinerarySegmentType.MiddleLocation:
    case ItinerarySegmentType.EndLocation:
    case ItinerarySegmentType.Ferry:
    case ItinerarySegmentType.Flight:
    case ItinerarySegmentType.DateRange:
    case ItinerarySegmentType.NoTransport:
    case ItinerarySegmentType.Upsell:
    case ItinerarySegmentType.UpsellNoTransport:
    case ItinerarySegmentType.StenaUpsellNoTransport:
      return false;

    case ItinerarySegmentType.Car:
    case ItinerarySegmentType.Cycle:
    case ItinerarySegmentType.Walk:
      return true;

    default:
      exhaustiveGuard(segment, 'Not known segment type');
  }
}

export function isItineraryLocationLoaderSegment(
  segment: ItineraryLoaderSegment
): segment is ItineraryLocationLoaderSegment {
  switch (segment.segmentType) {
    case ItinerarySegmentType.OwnStay:
    case ItinerarySegmentType.Accommodation:
    case ItinerarySegmentType.Activity:
    case ItinerarySegmentType.Ferry:
    case ItinerarySegmentType.Flight:
    case ItinerarySegmentType.DateRange:
    case ItinerarySegmentType.Car:
    case ItinerarySegmentType.Cycle:
    case ItinerarySegmentType.Walk:
    case ItinerarySegmentType.NoTransport:
    case ItinerarySegmentType.Upsell:
    case ItinerarySegmentType.UpsellNoTransport:
    case ItinerarySegmentType.StenaUpsellNoTransport:
      return false;

    case ItinerarySegmentType.StartLocation:
    case ItinerarySegmentType.MiddleLocation:
    case ItinerarySegmentType.EndLocation:
      return true;

    default:
      exhaustiveGuard(segment, 'Not known segment type');
  }
}

export function isItineraryBookableProductLoaderSegment(
  segment: ItineraryLoaderSegment
): segment is ItineraryBookableProductLoaderSegment {
  switch (segment.segmentType) {
    case ItinerarySegmentType.Accommodation:
    case ItinerarySegmentType.Activity:
    case ItinerarySegmentType.Ferry:
    case ItinerarySegmentType.Flight:
      return true;

    case ItinerarySegmentType.OwnStay:
    case ItinerarySegmentType.StartLocation:
    case ItinerarySegmentType.MiddleLocation:
    case ItinerarySegmentType.EndLocation:
    case ItinerarySegmentType.DateRange:
    case ItinerarySegmentType.Car:
    case ItinerarySegmentType.Cycle:
    case ItinerarySegmentType.Walk:
    case ItinerarySegmentType.NoTransport:
    case ItinerarySegmentType.Upsell:
    case ItinerarySegmentType.UpsellNoTransport:
    case ItinerarySegmentType.StenaUpsellNoTransport:
      return false;

    default:
      exhaustiveGuard(segment, 'Not known segment type');
  }
}

export function isItineraryBookableTransportLoaderSegment(
  segment: ItineraryLoaderSegment
): segment is ItineraryBookableTransportLoaderSegment {
  switch (segment.segmentType) {
    case ItinerarySegmentType.Ferry:
    case ItinerarySegmentType.Flight:
      return true;

    case ItinerarySegmentType.OwnStay:
    case ItinerarySegmentType.Accommodation:
    case ItinerarySegmentType.Activity:
    case ItinerarySegmentType.StartLocation:
    case ItinerarySegmentType.MiddleLocation:
    case ItinerarySegmentType.EndLocation:
    case ItinerarySegmentType.DateRange:
    case ItinerarySegmentType.Car:
    case ItinerarySegmentType.Cycle:
    case ItinerarySegmentType.Walk:
    case ItinerarySegmentType.NoTransport:
    case ItinerarySegmentType.Upsell:
    case ItinerarySegmentType.UpsellNoTransport:
    case ItinerarySegmentType.StenaUpsellNoTransport:
      return false;

    default:
      exhaustiveGuard(segment, 'Not known segment type');
  }
}
